import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, useZoho } from '../../context';
import { Mail, Megaphone, Users, Inbox, Server, Settings as SettingsIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../AppShell/AppShell';
import { apiMailboxConnectUrl, apiMailboxes, apiInboxStats, apiMailerSettings } from '../../api';
import CampaignsTab from './CampaignsTab';
import ContactsTab from './ContactsTab';
import InboxTab from './InboxTab';
import MailboxesTab from './MailboxesTab';
import SettingsTab from './SettingsTab';
import styles from './Mailer.module.css';

/**
 * Cold-outreach mailer.
 *
 * Replaces the previous browser-driven blast, where a `for` loop in this
 * component sent every message with a fixed two-second delay and stopped dead
 * if the tab was closed. Sending now lives on the server: a scheduler releases
 * a handful of messages a day inside a working-hours window, spaced at random
 * intervals, and stops the moment someone replies.
 */
// Mailboxes sits first because it is now what everything else depends on: a
// campaign cannot send without one, and the daily capacity every campaign draws
// against is defined there.
const TABS = [
  { key: 'mailboxes', label: 'Mailboxes', icon: Server },
  { key: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { key: 'contacts', label: 'Contacts', icon: Users },
  { key: 'inbox', label: 'Inbox', icon: Inbox },
  { key: 'settings', label: 'Settings', icon: SettingsIcon },
];

function Mailer() {
  const { jwtToken } = useAuth();
  const { connected: zohoConnected, checked: zohoChecked, needsReauth, refresh: refreshZoho } = useZoho();
  const navigate = useNavigate();

  const [tab, setTab] = useState('campaigns');
  const [mailboxes, setMailboxes] = useState([]);
  const [capacity, setCapacity] = useState(null);
  const [defaultFrom, setDefaultFrom] = useState('');
  const [unread, setUnread] = useState(0);
  const [sendingEnabled, setSendingEnabled] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => { refreshZoho(); }, [refreshZoho]);

  // The mailbox pool plus the counters on the tab strip. Loaded once here
  // rather than per tab, so the campaign editor can size an allocation against
  // real remaining capacity without a second round trip.
  const loadContext = useCallback(async () => {
    if (!zohoConnected || !jwtToken) return;
    try {
      const [mailboxData, settingsData] = await Promise.all([
        apiMailboxes(jwtToken),
        apiMailerSettings(jwtToken),
      ]);
      const list = mailboxData.mailboxes || [];
      setMailboxes(list);
      setCapacity(mailboxData.totals || null);
      setDefaultFrom(settingsData.settings?.defaultFromAddress || list[0]?.address || '');
      setSendingEnabled(Boolean(settingsData.settings?.sendingEnabled));
      setLoadError('');
    } catch (e) {
      setLoadError(e.message || 'Could not load mailer configuration');
    }

    try {
      const stats = await apiInboxStats(jwtToken);
      setUnread(stats.unread || 0);
    } catch {
      // A failing inbox count must not block the whole page.
    }
  }, [zohoConnected, jwtToken]);

  useEffect(() => { loadContext(); }, [loadContext]);

  const connectZoho = async () => {
    setConnecting(true);
    try {
      const { url } = await apiMailboxConnectUrl(jwtToken, 'com');
      // The return trip lands on /redirect carrying only a code, so the region
      // and label needed to finish the exchange are parked here on the way out.
      sessionStorage.setItem('mailbox_connect', JSON.stringify({ region: 'com', label: '' }));
      window.location.href = url;
    } catch (e) {
      alert('Failed to start Zoho authorization: ' + e.message);
      setConnecting(false);
    }
  };

  if (!zohoChecked) {
    return (
      <AppShell>
        <div className="feature-loading-state">
          <span className="spinner" style={{ width: 36, height: 36 }} />
        </div>
      </AppShell>
    );
  }

  if (!zohoConnected) {
    return (
      <AppShell>
        <div className="feature-loading-state">
          <div className="card" style={{ maxWidth: 480, textAlign: 'center' }}>
            <Mail size={48} color="var(--accent)" style={{ margin: '0 auto 24px' }} />
            <h1 className="modal-title">
              {needsReauth ? 'Reconnect Zoho Account' : 'Connect Zoho Account'}
            </h1>
            <p className="text-muted" style={{ marginBottom: 24 }}>
              {needsReauth
                ? 'The stored Zoho authorization is no longer valid — it was revoked, expired, or was issued to a different Zoho app than this server is configured with. Reconnecting fixes it.'
                : 'Authorize your Zoho account once. The portal stores an offline refresh token, so campaigns keep sending on schedule without you reconnecting.'}
            </p>

            {needsReauth && (
              <div className={styles.warnBox} style={{ textAlign: 'left' }}>
                <div className={styles.boxTitle}>Before you reconnect</div>
                Reconnecting replaces the token shared by every environment pointed at this database.
                If this is a local dev server using a different Zoho client ID than production,
                reconnecting here will break production sending. Match the client ID first.
              </div>
            )}

            <div className={styles.infoBox} style={{ textAlign: 'left' }}>
              <div className={styles.boxTitle}>What this allows</div>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Send sequenced outreach on a throttled daily schedule</li>
                <li>Read replies so a sequence stops the moment someone answers</li>
                <li>Send client welcome, milestone and upload-request email</li>
              </ul>
            </div>
            <button
              className="btn btn-primary w-full"
              style={{ marginBottom: 12, justifyContent: 'center' }}
              onClick={connectZoho}
              disabled={connecting}
            >
              <Mail size={18} />
              {connecting ? 'Redirecting…' : needsReauth ? 'Reconnect Zoho Account' : 'Connect Zoho Account'}
            </button>
            <button className="btn btn-ghost w-full" style={{ justifyContent: 'center' }} onClick={() => navigate('/portal')}>
              Go Back
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
        <div className="container" style={{ maxWidth: 1100, margin: '0 auto' }}>
          <div className="page-header" style={{ marginBottom: 20 }}>
            <div>
              <h1 className="page-title">Mailer</h1>
              <p className="page-subtitle">
                Sequenced outreach across your sending domains, with per-mailbox limits, reply
                detection and automatic follow-ups
                {capacity?.dailyLimit > 0 && (
                  <> — <strong>{capacity.unallocated}</strong> of {capacity.dailyLimit} a day still unclaimed</>
                )}
              </p>
            </div>
          </div>

          {loadError && <div className={styles.dangerBox}>{loadError}</div>}

          {mailboxes.length === 0 && (
            <div className={styles.warnBox}>
              <div className={styles.boxTitle}>No mailboxes set up yet</div>
              Campaigns send from mailboxes, and each one carries its own daily limit. Add yours on{' '}
              <button
                className="btn btn-ghost btn-sm"
                style={{ padding: '0 4px', textDecoration: 'underline' }}
                onClick={() => setTab('mailboxes')}
              >
                the Mailboxes tab
              </button>{' '}
              to get started.
            </div>
          )}

          {!sendingEnabled && (
            <div className={styles.warnBox}>
              <div className={styles.boxTitle}>Sending is switched off</div>
              Campaigns will not send until you enable sending in{' '}
              <button
                className="btn btn-ghost btn-sm"
                style={{ padding: '0 4px', textDecoration: 'underline' }}
                onClick={() => setTab('settings')}
              >
                Settings
              </button>.
            </div>
          )}

          <div className={styles.tabs}>
            {TABS.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                className={`${styles.tab} ${tab === key ? styles.tabActive : ''}`}
                onClick={() => setTab(key)}
              >
                <Icon size={16} /> {label}
                {key === 'inbox' && unread > 0 && (
                  <span className={`${styles.tabCount} ${styles.tabCountAlert}`}>{unread}</span>
                )}
              </button>
            ))}
          </div>

          {tab === 'mailboxes' && (
            <MailboxesTab jwtToken={jwtToken} onChanged={loadContext} />
          )}
          {tab === 'campaigns' && (
            <CampaignsTab jwtToken={jwtToken} mailboxes={mailboxes} defaultFrom={defaultFrom} />
          )}
          {tab === 'contacts' && <ContactsTab jwtToken={jwtToken} />}
          {tab === 'inbox' && <InboxTab jwtToken={jwtToken} onUnreadChange={setUnread} />}
          {tab === 'settings' && (
            <SettingsTab
              jwtToken={jwtToken}
              mailboxes={mailboxes}
              onDisconnected={() => { refreshZoho(); }}
            />
          )}
        </div>
    </AppShell>
  );
}

export default Mailer;
